/**
 * Invoice-list column preferences — localStorage-backed (Phase 13 Cluster A).
 *
 * Persists per-user column visibility and order for the list view.
 * Stored shape: { order: string[]; hidden: string[] }
 *
 * Loader tolerates stored keys that are no longer known (filters them out)
 * and appends newly-introduced keys to the end in default state so adding
 * a column doesn't require a migration.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';

const STORAGE_KEY_PREFIX = 'invoice_list_columns';

function storageKeyFor(userId: string | null | undefined): string {
  // Per-user scoping: multiple accounts on the same browser must not share
  // column preferences. Anonymous fallback is used only before the auth
  // session resolves on first paint — `useInvoiceColumnPrefs` re-reads from
  // storage once the real userId arrives.
  return `${STORAGE_KEY_PREFIX}:${userId ?? 'anon'}`;
}

export interface ColumnPrefs {
  order: string[];
  hidden: string[];
}

function readFromStorage(key: string): ColumnPrefs | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      Array.isArray(parsed.order) &&
      Array.isArray(parsed.hidden) &&
      parsed.order.every((k: unknown) => typeof k === 'string') &&
      parsed.hidden.every((k: unknown) => typeof k === 'string')
    ) {
      return { order: parsed.order, hidden: parsed.hidden };
    }
  } catch {
    // Fall through to null
  }
  return null;
}

function writeToStorage(key: string, prefs: ColumnPrefs): void {
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // Quota exceeded / privacy mode — silently ignore. Prefs stay in memory.
  }
}

function reconcile(stored: ColumnPrefs, known: readonly string[]): ColumnPrefs {
  const knownSet = new Set(known);
  const order = stored.order.filter((k) => knownSet.has(k));
  // Append any known keys that aren't in the stored order (new columns).
  for (const k of known) {
    if (!order.includes(k)) order.push(k);
  }
  const hidden = stored.hidden.filter((k) => knownSet.has(k));
  return { order, hidden };
}

export function useInvoiceColumnPrefs(
  defaultOrder: readonly string[]
): [ColumnPrefs, (next: ColumnPrefs) => void] {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const storageKey = storageKeyFor(userId);

  const [prefs, setPrefs] = useState<ColumnPrefs>(() => {
    const stored = readFromStorage(storageKey);
    if (stored) return reconcile(stored, defaultOrder);
    return { order: [...defaultOrder], hidden: [] };
  });

  // Re-read when the user identity changes (login, account switch) so prefs
  // are per-account, not per-browser. Also re-reconcile when defaultOrder
  // grows (new column shipped in a later release).
  useEffect(() => {
    const stored = readFromStorage(storageKey);
    if (stored) {
      setPrefs(reconcile(stored, defaultOrder));
    } else {
      setPrefs({ order: [...defaultOrder], hidden: [] });
    }
  }, [storageKey, defaultOrder]);

  const save = useCallback(
    (next: ColumnPrefs) => {
      setPrefs(next);
      writeToStorage(storageKey, next);
    },
    [storageKey]
  );

  return [prefs, save];
}

export function toggleColumn(prefs: ColumnPrefs, key: string): ColumnPrefs {
  const hidden = prefs.hidden.includes(key)
    ? prefs.hidden.filter((k) => k !== key)
    : [...prefs.hidden, key];
  return { ...prefs, hidden };
}

export function moveColumn(
  prefs: ColumnPrefs,
  key: string,
  direction: 'up' | 'down'
): ColumnPrefs {
  const idx = prefs.order.indexOf(key);
  if (idx === -1) return prefs;
  const target = direction === 'up' ? idx - 1 : idx + 1;
  if (target < 0 || target >= prefs.order.length) return prefs;
  const next = [...prefs.order];
  [next[idx], next[target]] = [next[target], next[idx]];
  return { ...prefs, order: next };
}

export function resetColumnPrefs(defaultOrder: readonly string[]): ColumnPrefs {
  return { order: [...defaultOrder], hidden: [] };
}

/**
 * Swap a column with its left or right **visible** neighbour, skipping hidden
 * columns in between. This is what users expect when they click "nach links"
 * / "nach rechts" in a column-header menu — movement happens within the
 * rendered order, not the underlying full-order array.
 */
export function moveColumnInVisibleOrder(
  prefs: ColumnPrefs,
  visibleOrder: readonly string[],
  key: string,
  direction: 'left' | 'right'
): ColumnPrefs {
  const visibleIdx = visibleOrder.indexOf(key);
  if (visibleIdx === -1) return prefs;
  const targetVisibleIdx = direction === 'left' ? visibleIdx - 1 : visibleIdx + 1;
  if (targetVisibleIdx < 0 || targetVisibleIdx >= visibleOrder.length) return prefs;
  const targetKey = visibleOrder[targetVisibleIdx];
  const next = [...prefs.order];
  const fromIdx = next.indexOf(key);
  const toIdx = next.indexOf(targetKey);
  if (fromIdx === -1 || toIdx === -1) return prefs;
  [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
  return { ...prefs, order: next };
}
